// Unity Services Dashboard base URLs by environment. Only staging and
// production are publicly reachable, so dev/test point at staging.
const DASHBOARD_URLS = {
  DEVELOPMENT: 'https://staging.cloud.unity.com',
  TEST: 'https://staging.cloud.unity.com',
  STAGING: 'https://staging.cloud.unity.com',
  PRODUCTION: 'https://cloud.unity.com',
} as const;

export type BuildManageSeatUrlArgs = {
  env: string | undefined;
  // Required: My Products is org-scoped. Callers must guard for a missing org
  // before building the URL (see useUpmProductDetailPage / useMyAssetsPage).
  organizationId: string;
  // Optional deep-link inputs from the entitlement expansion. Present only for
  // subscription-acquired entitlements with an inventory item.
  subscriptionId?: string;
  productVariantKey?: string;
};

// "Manage seat" sends the user to the dashboard's My Products (inventory) page
// for the given organization, where seats are managed. When both the
// subscription ID and product variant key are present, it deep-links straight
// to that product's seat-management page; missing either one falls back to the
// org-level My Products list.
export const buildManageSeatUrl = ({
  env,
  organizationId,
  subscriptionId,
  productVariantKey,
}: BuildManageSeatUrlArgs): string => {
  const envKey = (env ?? 'staging')
    .trim()
    .toUpperCase() as keyof typeof DASHBOARD_URLS;
  const baseUrl = DASHBOARD_URLS[envKey] ?? DASHBOARD_URLS.STAGING;
  const myProducts = `${baseUrl}/organizations/${encodeURIComponent(organizationId)}/commerce/my-products`;

  if (subscriptionId && productVariantKey) {
    return `${myProducts}/${encodeURIComponent(subscriptionId)}/products/${encodeURIComponent(productVariantKey)}`;
  }

  return `${myProducts}#unity-assetstore-product`;
};

// Opens the Manage seat URL in a new tab. Don't pass `noopener` in the features
// string: with it, window.open returns null even on success, so we can't tell a
// real block apart from a normal open. Open plainly, sever the opener ourselves
// for tabnabbing safety, and only fall back to same-tab nav when the popup was
// actually blocked.
export const openManageSeatUrl = (url: string): void => {
  const opened = window.open(url, '_blank');
  if (opened) {
    opened.opener = null;
  } else {
    window.location.assign(url);
  }
};
